import { fetchWithDefaults } from '@/lib/http/client';

export interface ChatProgressData {
  status?: string;
  complete?: boolean;
  chain_status?: string;
  result?: { error?: string | null };
}

export interface ChatRunPollClient {
  fetchChatProgress(
    conversationId: string,
    taskId: string
  ): Promise<ChatProgressData>;
}

export interface ChatRunContext {
  key: string;
  orgId: string;
  conversationId: string;
  taskId: string;
  pollCount: number;
  suppressToast: boolean;
}

export interface ChatRunHandlers {
  onComplete?: (ctx: ChatRunContext) => void | Promise<void>;
  onFailed?: (
    ctx: ChatRunContext,
    reason: 'failed' | 'timeout',
    serverMessage?: string
  ) => void;
  onSuperseded?: (ctx: {
    key: string;
    oldTaskId: string;
    newTaskId: string;
  }) => void;
  onEvicted?: (ctx: { key: string; evictedKey: string }) => void;
  onPollFailed?: (ctx: {
    key: string;
    conversationId: string;
    error: unknown;
  }) => void;
}

/**
 * Thrown by the default poll client when the progress endpoint responds
 * 429 (rate limited). `retryAfterMs`, when derivable from the response's
 * `Retry-After` header, tells the caller how long to wait before the next
 * poll attempt.
 */
export class ChatProgressRateLimitedError extends Error {
  retryAfterMs?: number;

  constructor(message: string, options?: { retryAfterMs?: number }) {
    super(message);
    this.name = 'ChatProgressRateLimitedError';
    this.retryAfterMs = options?.retryAfterMs;
  }
}

export interface ClassifyChatProgressOptions {
  /**
   * Whether a `paused` chain (waiting for human input) counts as a terminal
   * completion. A proposal chat turn surfaces as `paused` — "turn done, ready
   * for your review" — so the proposal tracker opts in. A main-chat reasoning
   * run pauses mid-run for human input and then *resumes*, so it must keep
   * polling until a genuine terminal status rather than finalize early.
   * Defaults to `false`.
   */
  treatPausedAsComplete?: boolean;
}

export function classifyChatProgress(
  d: ChatProgressData,
  options?: ClassifyChatProgressOptions
): 'complete' | 'failed' | 'in_progress' {
  const isFailed = d.chain_status === 'failed';
  if (isFailed) {
    return 'failed';
  }
  const treatPausedAsComplete = options?.treatPausedAsComplete ?? false;
  const isComplete =
    d.status === 'completed' ||
    d.complete === true ||
    d.chain_status === 'completed' ||
    (treatPausedAsComplete && d.chain_status === 'paused');
  return isComplete ? 'complete' : 'in_progress';
}

/**
 * The largest delay a browser `setTimeout` can store without overflowing its
 * 32-bit signed representation (~24.8 days). A larger value wraps around and
 * fires almost immediately, turning a very large `Retry-After` into a rapid
 * retry loop, so every parsed delay is clamped to this ceiling. (The tracker's
 * wall-clock watchdog resolves a genuinely-stuck run long before this bound.)
 */
const MAX_TIMER_DELAY_MS = 2_147_483_647;

/**
 * Parses an HTTP `Retry-After` header into a non-negative millisecond delay.
 * The header takes one of two forms (RFC 9110 §10.2.3): delta-seconds (a
 * non-negative integer) or an HTTP-date. Returns `undefined` when the header
 * is absent or unparseable, so the caller falls back to its own backoff.
 */
export function parseRetryAfterMs(
  retryAfterHeader: string | null
): number | undefined {
  if (retryAfterHeader === null) {
    return undefined;
  }
  const trimmed = retryAfterHeader.trim();
  if (trimmed === '') {
    return undefined;
  }
  const seconds = Number(trimmed);
  if (!Number.isNaN(seconds)) {
    // Numeric form: only a non-negative integer delta-seconds is valid. A
    // negative or fractional number is malformed — don't reinterpret it as a
    // date (Date.parse would coerce e.g. "-5" into a year).
    return Number.isInteger(seconds) && seconds >= 0
      ? Math.min(seconds * 1000, MAX_TIMER_DELAY_MS)
      : undefined;
  }
  // HTTP-date form (e.g. "Wed, 21 Oct 2026 07:28:00 GMT"). Parse the absolute
  // date and derive a delay relative to now, clamped to a non-negative value
  // (a past date means "retry immediately") and to the timer ceiling.
  const dateMs = Date.parse(trimmed);
  if (!Number.isNaN(dateMs)) {
    return Math.min(Math.max(dateMs - Date.now(), 0), MAX_TIMER_DELAY_MS);
  }
  return undefined;
}

export function createDefaultPollClient(orgId: string): ChatRunPollClient {
  return {
    async fetchChatProgress(conversationId, taskId) {
      const response = await fetchWithDefaults(
        `/api/orgs/${orgId}/reasoning/conversations/${conversationId}/progress/${taskId}/`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      if (response.status === 404) {
        return { status: 'completed' };
      }
      if (response.status === 429) {
        throw new ChatProgressRateLimitedError('rate limited', {
          retryAfterMs: parseRetryAfterMs(response.headers.get('Retry-After')),
        });
      }
      if (!response.ok) {
        throw new Error('progress ' + response.status);
      }
      return response.json();
    },
  };
}
